import { useRef, createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "./AuthContext";
import {canAction} from '../../components/ui/PermissionButton';

const InventoryContext = createContext();

export const InventoryProvider = ({ children }) => {
  const { user,permissions } = useAuth();
  const canRead=canAction({user,permissions},'inventory','view');

  const [inventory, setInventory] = useState([]);
  const [valuationMethod, setValuationMethod] = useState("FIFO");
  const [batches, setBatches] = useState([]);

  const isSavingRef = useRef(false);

  const fetchItems = async (signal) => {
    if(!canRead)return;
    try {
      const response = await fetch("/api/inventory/items", {
        credentials: "include",
        signal,
      });

      if (!response.ok) {
        throw new Error("Failed to fetch inventory items");
      }

      const data = await response.json();

      if (data.status === "success"&&!signal?.aborted) {
        setInventory(data.data || []);
      }
    } catch (error) {
      if(error.name==='AbortError')return;
      setInventory([]);
      console.error("Fetch Items Error:", error);
    }
  };

  const fetchBatches = async (signal) => {
    if(!canRead)return;
    try {
      const response = await fetch("/api/inventory/batches", {
        credentials: "include",
        signal,
      });

      if (!response.ok) {
        throw new Error("Failed to fetch inventory batches");
      }

      const data = await response.json();

      if (data.status === "success"&&!signal?.aborted) {
        setBatches(data.data || []);
      }
    } catch (error) {
      if(error.name==='AbortError')return;
      setBatches([]);
      console.error("Fetch Batches Error:", error);
    }
  };

  useEffect(() => {
    const request=new AbortController();
    setInventory([]);setBatches([]);
    if (user&&canRead) {
      fetchItems(request.signal);
      fetchBatches(request.signal);
    }
    return()=>request.abort();
  }, [user,canRead]);

  const addNewItem = async (item) => {
    if (isSavingRef.current) {
      return {
        status: "error",
        message: "Please wait. The item is already being saved.",
      };
    }

    isSavingRef.current = true;

    try {
      const response = await fetch("/api/inventory/items", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          ...item,
          name: item.name,
          department_id: Number(item.department_id),
          category: item.category,
          type: item.type,
          minStock: Number(item.minStock || 0),
          branch_id: Number(item.branch_id),
        }),
      });

      const data = await response.json();

      if (data.status !== "success") {
        return data;
      }

      // Opening stock and item creation are committed atomically by the backend.
      await fetchItems();
      await fetchBatches();

      return {
        status: "success",
        id: data.id,
      };
    } catch (error) {
      console.error("Add Item Error:", error);

      return {
        status: "error",
        message: "Could not save inventory item.",
      };
    } finally {
      isSavingRef.current = false;
    }
  };

  const getConsumableBatches = (itemName) => {
    const item = inventory.find((inventoryItem) => inventoryItem.name === itemName);

    if (!item) return [];

    const itemBatches = batches.filter((batch) => batch.item_id === item.id);

    if (valuationMethod === "FIFO") {
      itemBatches.sort(
        (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      );
    } else if (valuationMethod === "LIFO") {
      itemBatches.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
      );
    }

    return itemBatches;
  };

  return (
    <InventoryContext.Provider
      value={{
        inventory,
        addNewItem,
        fetchItems,
        fetchBatches,
        valuationMethod,
        setValuationMethod,
        getConsumableBatches,
      }}
    >
      {children}
    </InventoryContext.Provider>
  );
};

export const useInventory = () => useContext(InventoryContext);
